import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Explicit } from '../../components/ui/Explicit';
import { Card, Chip } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer, type PlayableTrack } from '../../stores/player';

export interface TrackSummary extends PlayableTrack {
  slug: string;
  /**
   * The artist's own words. `TrackRepository::shape()` has always returned it;
   * this interface simply never said so, which is how the dashboard's hero came
   * to render a field TypeScript did not know existed.
   */
  description: string | null;
  favorited: boolean;
  reposted?: boolean;
  repost_count?: number;
  created_at: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  failure_reason: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  /** Declared by the artist, never guessed. See the migration. */
  explicit: boolean;
  play_count: number;
  like_count: number;
}

/**
 * Somebody's tracks, newest first.
 *
 * ## Why unfinished tracks are shown at all
 *
 * To a visitor this is a list of playable tracks. To the owner it also includes
 * the ones still processing and the ones that failed - because otherwise the
 * upload flow ends with the track apparently vanishing for two minutes, and the
 * only honest reading of that is "it did not work".
 *
 * The server decides who sees which; this component renders what it is given
 * and never filters. A client-side filter would be a second, weaker copy of a
 * visibility rule that already exists (§6.7).
 */
export function TrackList({ username, isOwner }: { username: string; isOwner: boolean }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const { data, isPending } = useQuery({
    queryKey: ['tracks', username],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${username}/tracks`),
    /*
     * Poll while anything is still being worked on.
     *
     * Only while - a profile of finished tracks does not need a request every
     * three seconds. This is what makes "upload, then land back here" work
     * without a reload, and it stops on its own once everything has settled.
     */
    refetchInterval: (query) =>
      query.state.data?.items.some((track) => track.status === 'processing' || track.status === 'pending')
        ? 3000
        : false,
  });

  if (isPending) {
    return (
      // Skeletons, not a spinner: this list sits above the rest of a profile,
      // so a short spinner box that becomes a tall list shoves everything below
      // it down at the exact moment somebody starts reading.
      <div className="mt-4 flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <Card className="mt-4 p-6">
        <p className="text-sm text-soft">
          {isOwner ? (
            <>
              Nothing here yet.{' '}
              <Link to="/upload" className="text-accent hover:underline">
                {copy['cta.upload']}
              </Link>{' '}
              to get started.
            </>
          ) : (
            `@${username} hasn't posted any tracks yet.`
          )}
        </p>
      </Card>
    );
  }

  // The playable ones, in order, so pressing play on any of them queues the
  // rest of the profile behind it rather than stopping after one.
  const queue = items.filter((track) => track.status === 'ready');

  return (
    <ul className="mt-4 flex flex-col gap-2" data-track-list>
      {items.map((track) => {
        const isCurrent = current?.id === track.id;
        const ready = track.status === 'ready';

        return (
          // The identifier goes on the `li`, not the Card: Card takes only the
          // props it declares, so anything else is silently dropped.
          <li key={track.id} data-track-id={track.id}>
            <Card className="flex items-center gap-4 p-4">
              <TrackThumb track={track} />

              <button
                type="button"
                disabled={!ready}
                aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                onClick={() => (isCurrent ? toggle() : void play(track, queue, 'profile'))}
                className={[
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
                  'transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  ready ? 'bg-accent text-accent-ink hover:bg-accent-hover' : 'bg-raised text-faint',
                ].join(' ')}
              >
                {isCurrent && playing ? <PauseGlyph /> : <PlayGlyph />}
              </button>

              <div className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  {track.explicit && <Explicit />}
                  <DrawerLink
                    to={`/tracks/${track.id}`}
                    className="min-w-0 truncate text-sm font-medium text-ink hover:underline"
                  >
                    {track.title}
                  </DrawerLink>
                </span>

                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="meta-caps">{duration(track.duration_ms)}</span>

                  {ready && (
                    <span className="meta-caps">
                      {track.play_count.toLocaleString()}{' '}
                      {track.play_count === 1 ? 'play' : 'plays'}
                    </span>
                  )}

                  {/*
                    Only the owner is ever sent a visibility other than public,
                    so this badge appears for nobody else by construction.
                  */}
                  {track.visibility !== 'public' && (
                    <Chip>{track.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                  )}

                  {track.status === 'processing' && <Chip tone="signal">Processing</Chip>}
                  {track.status === 'pending' && <Chip>Waiting for upload</Chip>}
                  {track.status === 'failed' && <Chip tone="hot">Failed</Chip>}
                </div>

                {/*
                  The reason, to the owner only. "Failed" with no explanation is
                  the most frustrating possible outcome for somebody who just
                  waited through an upload.
                */}
                {track.status === 'failed' && isOwner && track.failure_reason && (
                  <p className="mt-1.5 text-xs text-faint">{track.failure_reason}</p>
                )}
              </div>

              {/* Only on tracks that exist to be heard. A heart on something
                  still processing is an action with nothing behind it. */}
              {ready && (
                <FavoriteButton
                  trackId={track.id}
                  favorited={track.favorited}
                  likeCount={track.like_count}
                  showCount
                  size="sm"
                />
              )}

              {/*
                The one thing an owner could not do to their own upload.
                Deliberately last and deliberately quiet - it is destructive,
                and a delete competing for attention with a play button is a
                delete somebody presses by accident.
              */}
              {isOwner && <DeleteTrack track={track} />}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The cover, at 200px, or a placeholder.
 *
 * A placeholder rather than nothing: without it rows with and without art have
 * different heights and the list stops scanning as a column.
 */
function TrackThumb({ track }: { track: TrackSummary }) {
  const src = track.artwork_small ?? track.artwork;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover"
      />
    );
  }

  return (
    <div
      aria-hidden
      className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
      style={{ backgroundImage: 'repeating-linear-gradient(45deg, #151530 0 6px, #101024 6px 12px)' }}
    />
  );
}

/** `4:07`, or an em dash while the duration is still unknown. */
function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
      <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <rect x="4" y="2.5" width="3" height="11" rx="1" />
      <rect x="9" y="2.5" width="3" height="11" rx="1" />
    </svg>
  );
}

/**
 * Deleting your own track.
 *
 * ## Two presses, no dialog
 *
 * The button becomes its own confirmation. A modal would be the conventional
 * answer and is worse here: this sits inside a list row, and a dialog that
 * covers the list makes you confirm the deletion of something you can no longer
 * see. Arming in place keeps the title next to the button that removes it.
 *
 * It disarms on a timer, so an armed button somebody walked away from does not
 * stay armed for the next person to click.
 *
 * ## What it says
 *
 * "Delete for good", not "Delete". The audio, the original, the artwork and the
 * waveform all go, and so do the plays and comments - `DeleteTrackAction` spells
 * out what survives and why. None of it comes back, and the button should not
 * imply otherwise.
 */
function DeleteTrack({ track }: { track: TrackSummary }) {
  const [armed, setArmed] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);

    return () => clearTimeout(timer);
  }, [armed]);

  const remove = useMutation({
    mutationFn: () => api.delete(`/tracks/${track.id}`),
    onSuccess: async () => {
      // The profile's counts move too, so the whole profile is refetched
      // rather than the list alone.
      await queryClient.invalidateQueries({ queryKey: ['tracks'] });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return (
    <button
      type="button"
      data-delete-track={track.id}
      disabled={remove.isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        if (armed) remove.mutate();
        else setArmed(true);
      }}
      className={`shrink-0 rounded-[var(--radius-chip)] border px-2.5 py-1.5 font-mono text-[10px] transition-colors ${
        armed
          ? 'border-hot-edge bg-hot-wash text-hot-text'
          : 'border-transparent text-ghost hover:border-line hover:text-soft'
      }`}
    >
      {remove.isPending ? 'deleting…' : armed ? 'delete for good?' : 'delete'}
    </button>
  );
}
